import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type PageOptions, usePage } from "@/api/pagination";
import WebhookService from "@/api/services/webhooks";
import { useToast } from "@/providers/ToastProvider";
import { ExistingWebhook, Webhook } from "@/types";

export function useWebhooks(params: PageOptions = {}) {
  return usePage<ExistingWebhook>(["webhooks"], WebhookService.getWebhooks, params);
}

export function useWebhook(webhookId: ExistingWebhook["id"]) {
  return useQuery({
    queryKey: ["webhooks", webhookId],
    queryFn: () => WebhookService.getWebhook(webhookId),
  });
}

export function useCreateWebhook() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { webhook: Webhook; redirectPath?: string }) => WebhookService.postWebhook(payload.webhook),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
      addToast({
        kind: "SUCCESS",
        message: `Webhook created successfully!`,
      });
    },
  });
}

export function useUpdateWebhook() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { webhookId: ExistingWebhook["id"]; webhook: Partial<Webhook>; redirectPath?: string }) =>
      WebhookService.patchWebhook(payload.webhookId, payload.webhook),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
      queryClient.invalidateQueries({
        queryKey: ["webhooks", variables.webhookId],
      });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
      addToast({
        kind: "SUCCESS",
        message: `Webhook updated successfully!`,
      });
    },
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: { webhookId: ExistingWebhook["id"]; redirectPath?: string }) =>
      WebhookService.deleteWebhook(payload.webhookId),
    onSuccess: (_data, variables) => {
      // Remove deleted webhook
      queryClient.removeQueries({
        queryKey: ["webhooks", variables.webhookId],
      });

      // Refetch all webhooks
      queryClient.invalidateQueries({
        queryKey: ["webhooks"],
      });
      if (variables.redirectPath) {
        navigate(variables.redirectPath);
      }
    },
  });
}
